import AwardsSection from "@components/new-ui/AwardsSection";
import ClientLogos from "@components/new-ui/ClientLogos";
import { container_classname } from "@components/new-ui/constants";
import CTASection from "@components/new-ui/CTASection";
import FAQSection, { FAQSectionProps } from "@components/new-ui/FAQSection";
import Herosection, { HerosectionProps } from "@components/new-ui/IndustriesSection/Herosection";
import NewLayout from "@components/new-ui/Layout";
import TestimonialSection from "@components/new-ui/TestimonialSection";
import Image from "next/image";
import React from "react";

const herosectionData: HerosectionProps = {
    chipTitle: "COMPARE",
    title: "Gallabox vs. Doubletick.io: Double-check & choose the right WhatsApp business solution",
    description: "Discover why growing teams choose Gallabox for deeper automation, richer integrations, and more powerful customer conversations!",
    primaryButtonText: "Start free trial",
    imageUrl: "https://assets.gallabox.com/gb-home/compare/doubletick/doubletick-hero.png",
};

const defaultFAQData: FAQSectionProps = {
    title: "Got questions? Perhaps these FAQs can help!",
    faqItems: [
        {
            question: "How do Gallabox and Doubletick.io integrate with third-party apps?",
            answer: (
                <div>
                    Both Gallabox and Doubletick.io offer integrations with a wide range of third-party payment gateways, e-commerce platforms, CRMs, etc. The steps to connect with
                    these apps are also easy. But Gallabox offers enhanced workflows for payments and virtual stores, making it user-friendly.
                </div>
            ),
        },
        {
            question: "Which platform offers a more user-friendly interface?",
            answer: (
                <>
                    Gallabox has an intuitive interface, making it easy to use for individuals across teams. Its no-code chatbot builders and ready-to-use templates make it
                    user-friendly for people of all technical backgrounds. Doubletick.io, on the other hand, has a basic interface and may require a learning curve.
                </>
            ),
        },
        {
            question: "How do both platforms handle large-scale message broadcasts?",
            answer: (
                <p>
                    Gallabox allows large-scale message broadcasting with message personalisation and lead qualification, and the option to assign the conversation either to an
                    individual user, teams, or bots. Doubletick.io’s broadcast software has very limited features.
                </p>
            ),
        },
    ],
};

const comparisonTable: { features: string | JSX.Element; wati: string | JSX.Element; gallabox: string | JSX.Element; gallaboxstdOut: string | JSX.Element }[] = [
    {
        features: "Third-party integrations",
        wati: "Supports about 10 integrations.",
        gallabox: "Connect WhatsApp with 30 different apps, so you can manage everything without switching tools.",
        gallaboxstdOut: "More integrations mean smoother workflows and fewer hassles.",
    },

    {
        features: "Security & Compliance",
        wati: "Offers number masking and role-based access controls.",
        gallabox: "Enterprise-level security with SOC2, AES-256 encryption, and multi-factor authentication.",
        gallaboxstdOut: "Gallabox offers the best in the industry, highest standard security protections.",
    },

    {
        features: "Broadcasting & Messaging",
        wati: "Unlimited bulk messaging with scheduling and quick call-to-action buttons.",
        gallabox: "Personalized message templates and the ability to capture audiences directly from Facebook and Instagram ads.",
        gallaboxstdOut: "Gallabox adds lead qualification and deeper personalization based on customer behavior and journey stage.",
    },

    {
        features: "Seamless workflows",
        wati: "Provides workflow automation primarily through predefined commerce bot actions with limited customization and integration depth.",
        gallabox: "Offers no-code, drag-and-drop workflow automation that connects chatbots, CRM, and e-commerce actions to create end-to-end customer journeys inside WhatsApp.",
        gallaboxstdOut: "Gallabox has better backend integration, making it more scalable for teams running advanced sales and support workflows.",
    },
];

const comparisonTableheader = {
    heading1: "Feature",
    image1: "https://assets.gallabox.com/gb-home/compare/doubletick/doubletick.png",
    heading2: "Why Gallabox stands out",
    image2: "https://assets.gallabox.com/gb-home/compare/doubletick/gallabox.png",
};
const DoubleTick: React.FC = () => {
    return (
        <NewLayout
            title="Gallabox vs Doubletick.io: WhatsApp API Comparison 2025"
            description="Compare Gallabox vs Doubletick.io in 2025 to find the ideal WhatsApp Business API solution. Evaluate features, pricing, and automation capabilities."
            canonical="https://gallabox.com/compare/doubletick"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/compare/doubletick/doubletick-og.png" }}
        >
            <Herosection herosection={herosectionData} />
            <section className={`${container_classname} my-10`}>
                <h3 className="text-center text-lg font-normal py-5">TRUSTED BY 10,000+ BUSINESSES</h3>
                <ClientLogos />
            </section>
            <div className="py-20 md:block hidden" style={{ backgroundImage: "linear-gradient(103deg, #EDF4FF 28.71%, #D2DDFF 84.45%, #84A2FF 136.43%)" }}>
                <h5 className="max-w-6xl mx-auto text-6xl mb-10" style={{ fontFamily: "Degular Variable" }}>
                    Difference between Gallabox vs. Doubletick.io - How Gallabox stands out
                </h5>
                <div className="grid grid-cols-4 md:max-w-6xl md:mx-auto border-t border-r border-l border-solid border-gray-100 bg-white">
                    <div className="flex p-6 bg-white">
                        <p className="my-auto font-bold text-xl">{comparisonTableheader.heading1}</p>
                    </div>
                    <div className="flex bg-gray-50 border-r border-solid border-gray-100  p-6">
                        <Image src={comparisonTableheader.image1} width={180} height={20} alt="" />
                    </div>
                    <div className="flex bg-gray-50  p-6">
                        <Image src={comparisonTableheader.image2} width={180} height={20} alt="" />
                    </div>
                    <div className=" flex  font-bold text-xl  p-6">
                        <p className="my-auto">{comparisonTableheader.heading2}</p>
                    </div>
                </div>
                {comparisonTable.map((data, ind) => {
                    return (
                        <div className="grid grid-cols-4 md:max-w-6xl md:mx-auto border border-solid border-gray-100" key={ind}>
                            <div className="flex p-5 bg-white">
                                <p className="my-auto">{data.features}</p>
                            </div>
                            <div className="flex bg-gray-50 border-r border-solid border-gray-100 p-5">
                                <p className="my-auto">{data.wati}</p>
                            </div>
                            <div className="flex bg-gray-50 p-5">
                                <p className="my-auto">{data.gallabox}</p>
                            </div>
                            <div className=" flex  p-5 bg-white">
                                <p className="my-auto">{data.gallaboxstdOut}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
            <section className="md:hidden block py-20">
                <h5 className="text-center text-xl mb-10" style={{ fontFamily: "Degular Variable" }}>
                    Difference between Gallabox vs. Doubletick.io - How Gallabox stands out
                </h5>
                <div className="grid grid-cols-3 items-center border border-solid border-gray-400 mx-5">
                    <Image src={comparisonTableheader.image1} width={100} height={20} alt="" />
                    <Image src={comparisonTableheader.image2} width={100} height={20} alt="" /> <p className="font-bold text-sm">Why gallabox Stands out</p>
                </div>
                <div>
                    <p>
                        {comparisonTable.map((data, ind) => {
                            return (
                                <div className="mx-5" key={ind}>
                                    <div className="bg-blue-50 border border-gray-300 border-solid font-semibold">{data.features}</div>
                                    <div className="grid grid-cols-3 gap-x-1 items-center border border-solid border-gray-400">
                                        <div>{data.wati}</div>
                                        <div>{data.gallabox}</div>
                                        <div>{data.gallaboxstdOut}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </p>
                </div>
            </section>
            <FAQSection faqData={defaultFAQData} />
            <TestimonialSection /> <AwardsSection />
            <CTASection title="Don’t settle for less. Scale smarter with Gallabox!" />
        </NewLayout>
    );
};

export default DoubleTick;
